<template>
  <div class="contact-search">
    <v-layout row wrap>

      <!-- intro !-->
      <v-flex xs12>
        <FormSectionTitle>
          {{formTitle}}
          <template
            v-if="isRealEstateProfessional && listType === 'CUSTOMER'"
            v-slot:suffix>
            (required)
          </template>
          <template v-else v-slot:suffix>
            (optional)
          </template>
        </FormSectionTitle>

        <span>
          {{formDescription}}
        </span>
      </v-flex>

      <!-- content !-->
      <v-flex xs12>
        <ContactDetailsBox
          v-for="appointmentContact in filteredAptContacts"
          :key="appointmentContact.personId"
          :contact="appointmentContact">
          <div class="remove-container">
            <a class="remove" @click="deleteContact(appointmentContact.personId)">Remove</a>
          </div>
        </ContactDetailsBox>

        <div
          class="contact-search--content">
          <!-- add account button !-->
          <ButtonCommon v-if="!addContact" border @click="clickAddButton">
            + Add {{displayAccountType}}
          </ButtonCommon>

          <template v-if="addContact">
            <!-- search form !-->
            <ContactSearchForm
              v-if="searchContact"
              :apiError="searchError"
              :email="searchEmail"
              :loading="searchLoading"
              @submit="searchAccount" />

            <!-- account create form !-->
            <template v-if="createContact">
              <BaseAlert hg-type="info" :hg-value="true">
                  We looked, but we didn't find {{createAlertContactLabel}}
                  with that email address. Please enter their information below.
              </BaseAlert>

              <ContactCreateForm
                :email="searchEmail"
                :loading="createLoading"
                :type="contactType"
                @editEmail="handleEditEmail"
                @submit="createAccount" />
            </template>
          </template>
        </div>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { Components, Constants } from '@spectorasoftware/homegauge-vue';
import { mapActions, mapGetters, mapState } from 'vuex';

import ContactDetailsBox from '@/components/ContactDetailsBox.vue';
import ButtonCommon from '@/components/common/ButtonCommon.vue';
import ContactCreateForm from '@/components/forms/ContactCreateForm.vue';
import ContactSearchForm from '@/components/forms/ContactSearchForm.vue';
import FormSectionTitle from '@/components/FormSectionTitle.vue';

const { BaseAlert } = Components;
const { AccountTypes } = Constants;

export default {
  components: {
    BaseAlert,
    ButtonCommon,
    ContactCreateForm,
    ContactDetailsBox,
    ContactSearchForm,
    FormSectionTitle,
  },
  props: {
    formTitle: {
      type: String,
      required: true,
    },
    formDescription: {
      type: String,
      required: true,
    },
    listType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      addContact: false,
      createContact: false,
      createError: null,
      createLoading: false,
      searchContact: false,
      searchEmail: '',
      searchError: null,
      searchLoading: false,
    };
  },
  computed: {
    ...mapState('Appointment', {
      appointmentContacts: state => state.contactComponentData.contacts,
    }),
    filteredAptContacts() {
      return this.appointmentContacts.filter(
        contact => contact.type === this.listType,
      );
    },
    ...mapGetters('Auth', ['personType']),
    createAlertContactLabel() {
      switch (this.contactType) {
        case Constants.AccountTypes.REAL_ESTATE_PROFESSIONAL:
          return `an ${this.displayAccountType}`;
        case Constants.AccountTypes.CUSTOMER:
        default:
          return `a ${this.displayAccountType}`;
      }
    },
    contactType() {
      return this.listType === AccountTypes.CUSTOMER
        ? AccountTypes.CUSTOMER
        : AccountTypes.REAL_ESTATE_PROFESSIONAL;
    },
    isRealEstateProfessional() {
      return this.personType === AccountTypes.REAL_ESTATE_PROFESSIONAL;
    },
    displayAccountType() {
      switch (this.contactType) {
        case Constants.AccountTypes.REAL_ESTATE_PROFESSIONAL:
          return 'agent';
        case Constants.AccountTypes.CUSTOMER:
        default:
          return 'client';
      }
    },
  },
  methods: {
    ...mapActions('Appointment', ['pushContact', 'removeContact']),
    ...mapActions('Person', [
      'findCustomer',
      'findRep',
      'lookupEmail',
      'makeAccount',
    ]),
    clickAddButton() {
      this.addContact = true;
      this.searchContact = true;
    },
    async createAccount(accountPayload) {
      this.createLoading = true;
      this.createError = null;

      try {
        const foundUser = await this.lookupEmail({
          email: accountPayload.email,
          type: accountPayload.type,
        });

        const createdUser = await this.makeAccount({
          ...accountPayload,
          accountType: accountPayload.type,
          token: foundUser.token,
        });
        await this.pushContact(createdUser);
        this.addContact = false;
        this.createContact = false;
      } catch (err) {
        this.createError = err;
      }

      this.createLoading = false;
    },
    handleEditEmail() {
      this.createContact = false;
      this.searchContact = true;
    },
    async deleteContact(id) {
      await this.removeContact(id);
    },
    async searchAccount(searchEmail) {
      this.searchEmail = searchEmail;
      this.searchError = null;
      this.searchLoading = true;

      try {
        const foundUser = this.listType === AccountTypes.CUSTOMER
          ? await this.findCustomer(searchEmail)
          : await this.findRep(searchEmail);
        if (foundUser && foundUser.exists) {
          await this.pushContact(foundUser);
          this.addContact = false;
        } else {
          this.toggleCreateForm();
        }
      } catch (err) {
        this.searchError = err;
      }

      this.searchLoading = false;
    },
    toggleCreateForm() {
      this.createContact = true;
      this.searchContact = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/vars/theme';
@import '../styles/vars/typography';

.remove-container {
  text-align: right;

  .remove {
    color: $theme-color-secondary;
    font-size: 1rem;
    font-weight: $typography-weight-bold;
  }
}
</style>
